import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { formatCents } from '@src/common';
import { getContrastColorFrom } from '@src/common/colors';
import { useWidgetsTheme as theme } from '@src/core/hooks';
import {
  BodyExtraSmall,
  BodyRegular,
  BodyRegularMedium,
  BodyRegularSemiBold,
  BodySmall,
} from '@src/fonts/typography/Typography';
import CheckboxIcon from '@src/icons/check-icon.svg';
import { DarkTextColor } from '@src/fonts/typography/TypographyColors';
import { device } from '@src/common/breakpoints';
import { useWidgetId } from '@src/core/IdProvider';
import { Additional, AdditionalType } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import { LayoutColors } from '@src/core/theme';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { useProductAdditional } from '@src/domains/products/hooks/useProductAdditional';

interface ProductAdderAdditionalCardProps {
  additional: Additional;
}

const ProductAdderAdditionalCard = ({ additional }: ProductAdderAdditionalCardProps) => {
  const id = useWidgetId();
  const actionColor = theme('actionColor');
  const { i18n } = useTranslate();
  const productId = useSelectedProductId();

  const { checked, input, quantity, availableAmount, isAvailable, quantityError, updateAdditional, trackToggle } =
    useProductAdditional({
      widgetId: id,
      productId: productId ?? undefined,
      additional,
      i18n,
    });

  const contrastColor = getContrastColorFrom(actionColor);
  const imageUrl = additional.image?.styles.find((style) => style.url);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handleCheckboxClick = () => {
    const nextChecked = !checked;
    trackToggle(nextChecked);
    updateAdditional({ checked: nextChecked, input: '', quantity: undefined });
  };

  const handleInput = (newInput: string) => {
    updateAdditional({ input: newInput });
  };

  const handleChangeQuantity = (value: string) => {
    const nextQuantity = value.trim() ? parseInt(value) : undefined;
    updateAdditional({ quantity: nextQuantity });
  };

  useEffect(() => {
    const { current } = textAreaRef;
    if (current) {
      current.style.height = 'inherit';
      const scrollHeight = current.scrollHeight;
      current.style.height = scrollHeight + 'px';
    }
  }, [input, textAreaRef]);

  useEffect(() => {
    if (additional.type === AdditionalType.PerQuantity && !checked) {
      updateAdditional({ quantity: undefined });
    }
  }, [checked, additional.type, updateAdditional]);

  return (
    <AdditionalCard key={additional.id} isNotAvailable={!isAvailable}>
      <AdditionalCardHeader>
        <AdditionalCardInformation>
          <AdditionalCardImage hidden={!imageUrl}>
            <img src={imageUrl?.url} alt={additional.title} />
          </AdditionalCardImage>
          <AdditionalCardText>
            <AdditionalCardTextName>{additional.title}</AdditionalCardTextName>
            <AdditionalCardTextPrice>
              <span>{formatCents(additional.priceCents)}</span>
            </AdditionalCardTextPrice>
            {availableAmount !== null ? (
              <Quantity>
                {i18n('adder_additionals_remain')} {availableAmount}
              </Quantity>
            ) : null}
          </AdditionalCardText>
        </AdditionalCardInformation>
        {isAvailable ? (
          <AdditionalCheckbox
            role='checkbox'
            aria-checked={checked}
            selected={checked}
            actionColor={actionColor}
            contrastColor={contrastColor}
            onClick={handleCheckboxClick}
            data-cy='productAdderAdditionalsCheckbox'
          >
            {checked ? <CheckboxIcon /> : null}
          </AdditionalCheckbox>
        ) : (
          <SoldOffWrapper>
            <NotAvailable>{i18n('adder_additionals_soldOff')}</NotAvailable>
          </SoldOffWrapper>
        )}
      </AdditionalCardHeader>

      <AdditionalCardBody>
        <AdditionalCardDescription hidden={!additional.description}>{additional.description}</AdditionalCardDescription>

        {additional.type === AdditionalType.PerQuantity && checked ? (
          <InputWrapper>
            <InputLabel>{i18n('adder_quantity')}</InputLabel>

            <AdditionalQuantityInput
              min={1}
              step={1}
              max={additional.maxQuantity ?? undefined}
              type='number'
              value={quantity}
              hasError={!!quantityError}
              aria-errormessage={quantityError}
              onChange={(e) => handleChangeQuantity(e.target.value)}
              placeholder={i18n('adder_additionals_custom_type_quantity_placeholder')}
            />

            {quantityError ? <QuantityInputHelper>{quantityError}</QuantityInputHelper> : null}
          </InputWrapper>
        ) : null}

        {additional.enableInput && checked ? (
          <InputWrapper>
            <InputLabel>{i18n('base_description')}</InputLabel>

            <AdditionalsInput
              ref={textAreaRef}
              value={input}
              onChange={(e) => handleInput(e.target.value)}
              placeholder={additional.inputLabel ?? undefined}
              hidden={!additional.enableInput || !checked}
              data-cy='productAdderAdditionalsInput'
            />
          </InputWrapper>
        ) : null}
      </AdditionalCardBody>
    </AdditionalCard>
  );
};

interface AdditionalCheckboxProps {
  actionColor: string;
  selected: boolean;
  contrastColor: string;
}

const AdditionalCard = styled.div<{ isNotAvailable?: boolean }>`
  background: #f6f6f6;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
`;
const AdditionalCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const AdditionalCardDescription = styled.p`
  ${BodySmall};
  ${device.smallTablet} {
    ${BodyRegular};
  }
  margin-top: 14px;
`;
const AdditionalCardInformation = styled.div`
  display: flex;
  align-items: start;
  gap: 8px;
`;

const AdditionalCardImage = styled.div`
  width: 88px;
  height: 88px;
  img {
    width: 100%;
    height: 100%;
    border-radius: 4px;
  }
`;

const AdditionalCardText = styled.div`
  display: flex;
  flex-direction: column;
`;

const AdditionalCardTextName = styled.p`
  ${BodySmall}
  ${DarkTextColor}
    ${device.smallTablet} {
    ${BodyRegularSemiBold}
  }
`;

const AdditionalCardTextPrice = styled.p`
  ${BodySmall}
  ${device.smallTablet} {
    ${BodyRegular}
  }
`;

const AdditionalsInput = styled.textarea`
  background: #ffffff;
  border: 1px solid #949494;
  min-height: 36px;
  resize: none;
  border-radius: 4px;
  overflow: hidden;
  ${BodyRegularSemiBold}
  padding: 6px 8px;
`;

const AdditionalQuantityInput = styled.input<{ hasError: boolean }>`
  background: #ffffff;
  border: 1px solid #949494;
  min-height: 36px;
  resize: none;
  border-radius: 4px;
  overflow: hidden;
  ${BodyRegularSemiBold}
  padding: 6px 8px;
  border-color: ${(props) => (props.hasError ? LayoutColors.errorMain : '#949494')};
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuantityInputHelper = styled.span`
  ${BodyExtraSmall}
  color: ${LayoutColors.errorMain}
`;

const AdditionalCheckbox = styled.div<AdditionalCheckboxProps>`
  width: 45px;
  height: 45px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease-in-out;
  border: 1px solid ${(props) => props.actionColor};
  background-color: ${(props) => (props.selected ? props.actionColor : 'transparent')};
  margin-right: 4px;
  margin-bottom: 12px;
  path {
    stroke: ${(props) => props.contrastColor};
  }
`;

const Quantity = styled.span`
  ${BodyRegular}
  margin-top: 20px;
`;

const SoldOffWrapper = styled.div`
  background-color: rgba(255, 255, 255, 0.5);

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 16px;
`;

const NotAvailable = styled.span`
  ${BodyRegularMedium}
  padding: 8px;
  background: #cdcdcd;
  border-radius: 4px;
`;

const AdditionalCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputLabel = styled.label`
  ${BodyRegular}
`;

export default ProductAdderAdditionalCard;
